import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { AttendanceApi, StaffAttendanceDailyRow } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useDefaultYear } from './useDefaultYear';
import { formatDate } from '@/lib/format';
import { StaffTypeBadge } from './staffTypeBadge';

const columnHelper = createColumnHelper<StaffAttendanceDailyRow>();

const BASE_COLUMNS = [
  columnHelper.accessor('date', { header: 'Date', cell: (ctx) => formatDate(ctx.getValue()) }),
  columnHelper.accessor('typeLabel', { header: 'Type', cell: (ctx) => <StaffTypeBadge type={ctx.row.original.type} label={ctx.getValue()} /> }),
  columnHelper.accessor('time', { header: 'Time', cell: (ctx) => ctx.getValue() ?? '—' }),
  columnHelper.accessor('remarks', { header: 'Remarks', cell: (ctx) => ctx.getValue() ?? '—' }),
];

/** One teacher's full day-by-day attendance history over a range. Same StaffAttendanceService.dailyList('teacher', ...) query as the Report page, filtered to one personId. */
export function DetailedTeacherAttendancePage() {
  const dt = useDataTable({ defaultLimit: 50, defaultSortBy: 'date', defaultSortOrder: 'asc' });
  const { selectedYear, isLoading: yearLoading } = useDefaultYear();

  const [personId, setPersonId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (selectedYear) {
      setStartDate(selectedYear.startDate.slice(0, 10));
      setEndDate(selectedYear.endDate.slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear?.id]);

  const { data: teachers = [] } = useQuery({
    queryKey: ['attendance', 'staff-people', 'teacher'],
    queryFn: () => AttendanceApi.staffPeople('teacher'),
  });

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['attendance', 'teachers-daily-detailed', dt.params, personId, startDate, endDate],
    queryFn: () => AttendanceApi.teachersDaily({ ...dt.params, personId, startDate: startDate || undefined, endDate: endDate || undefined }),
    enabled: !yearLoading && !!personId && !!startDate && !!endDate,
    placeholderData: keepPreviousData,
  });

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    ...BASE_COLUMNS,
  ];

  return (
    <>
      <PageHeader title="Detailed Teacher Attendance" description="One teacher's full attendance history over a date range — every date and status, not just totals." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-64" value={personId} onChange={(e) => setPersonId(e.target.value)}>
          <option value="">— Select a teacher —</option>
          {teachers.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
        <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      {!personId ? (
        <div className="card p-8 text-center text-slate-500">Select a teacher to view their attendance history.</div>
      ) : (
        <DataTable
          columns={columns}
          result={pageData}
          state={dt}
          loading={isFetching}
          emptyMessage="No attendance recorded for this teacher in the selected range."
        />
      )}
    </>
  );
}
